import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { 
  Users, 
  Building2, 
  IndianRupee, 
  TrendingUp,
  TrendingDown,
  Bed,
  Download
} from 'lucide-react';
import { Button } from '../ui/button';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer 
} from 'recharts';
import axios from 'axios';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const Dashboard = () => {
  const { token } = useAuth();
  const { selectedProperties = [], properties = [] } = useOutletContext() || {};
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      // Build query params with selected property IDs
      let url = `${API}/analytics/dashboard`;
      if (selectedProperties.length > 0 && selectedProperties.length < properties.length) {
        const propertyIds = selectedProperties.join(',');
        url += `?property_ids=${propertyIds}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [token, selectedProperties, properties.length]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const exportToExcel = async (type) => {
    try {
      let data = [];
      let filename = '';

      if (type === 'payments') {
        const response = await axios.get(`${API}/money/payments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        data = response.data;
        filename = 'payments_report.xlsx';
      } else if (type === 'expenses') {
        const response = await axios.get(`${API}/money/expenses`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        data = response.data;
        filename = 'expenses_report.xlsx';
      }

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, type);
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-6">
        <p className="text-gray-600">No analytics data available</p>
      </div>
    );
  }

  const { overview, financial, revenue_by_month, expenses_by_category, property_revenue } = analytics;

  // Prepare chart data
  const revenueChartData = Object.entries(revenue_by_month).map(([month, amount]) => ({
    month: month.substring(5), // Get MM from YYYY-MM
    revenue: amount
  })).reverse();

  const expenseChartData = Object.entries(expenses_by_category).map(([category, amount]) => ({
    name: category,
    value: amount
  }));

  const propertyRevenueData = property_revenue.slice(0, 5); // Top 5 properties

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Dashboard
          </h1>
          <p className="text-gray-600 mt-2 flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Real-time analytics and insights
          </p>
          {selectedProperties.length > 0 && selectedProperties.length < properties.length && (
            <p className="text-sm text-blue-600 mt-1 font-medium">
              📊 Showing data for {selectedProperties.length} selected {selectedProperties.length === 1 ? 'property' : 'properties'}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={() => exportToExcel('payments')} 
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all"
          >
            <Download className="mr-2 h-4 w-4" />
            Export Payments
          </Button>
          <Button 
            onClick={() => exportToExcel('expenses')} 
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg hover:shadow-xl transition-all"
          >
            <Download className="mr-2 h-4 w-4" />
            Export Expenses
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-xl hover:shadow-2xl transition-all hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Properties</p>
              <p className="text-4xl font-bold mt-2">{overview.total_properties}</p>
              <p className="text-blue-100 text-xs mt-2">Manage your portfolio</p>
            </div>
            <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-sm">
              <Building2 className="h-8 w-8 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-xl hover:shadow-2xl transition-all hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Total Beds</p>
              <p className="text-4xl font-bold mt-2">{overview.total_beds}</p>
              <p className="text-green-100 text-xs mt-2">
                <span className="font-semibold">{overview.occupied_beds}</span> occupied • 
                <span className="font-semibold"> {overview.vacant_beds}</span> vacant
              </p>
            </div>
            <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-sm">
              <Bed className="h-8 w-8 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-xl hover:shadow-2xl transition-all hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Occupancy Rate</p>
              <p className="text-4xl font-bold mt-2">{overview.occupancy_rate}%</p>
              <p className="text-purple-100 text-xs mt-2">
                <span className="font-semibold">{overview.active_tenants}</span> active tenants
              </p>
            </div>
            <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-sm">
              <Users className="h-8 w-8 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-yellow-500 to-orange-500 text-white border-0 shadow-xl hover:shadow-2xl transition-all hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-medium">Net Income</p>
              <p className="text-4xl font-bold mt-2">₹{(financial.net_income / 1000).toFixed(0)}K</p>
              <p className="text-yellow-100 text-xs mt-2">
                This month: <span className="font-semibold">₹{(financial.this_month_collected / 1000).toFixed(0)}K</span>
              </p>
            </div>
            <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-sm">
              <IndianRupee className="h-8 w-8 text-white" />
            </div>
          </div>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <Card className="p-6 bg-white shadow-xl border-0 hover:shadow-2xl transition-all">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              Revenue Trend
            </h3>
            <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
              Last 6 Months
            </span>
          </div>
          {revenueChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={revenueChartData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                <Tooltip 
                  formatter={(value) => `₹${value.toLocaleString()}`}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  fill="url(#colorRevenue)"
                  dot={{ fill: '#3b82f6', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
              <p className="text-gray-400">No revenue data yet. Start adding payments!</p>
            </div>
          )}
        </Card>

        {/* Expenses by Category */}
        <Card className="p-6 bg-white shadow-xl border-0 hover:shadow-2xl transition-all">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <div className="p-2 bg-red-100 rounded-lg">
                <TrendingDown className="h-5 w-5 text-red-600" />
              </div>
              Expenses Breakdown
            </h3>
            <span className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full font-medium">
              By Category
            </span>
          </div>
          {expenseChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={expenseChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {expenseChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => `₹${value.toLocaleString()}`}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
              <p className="text-gray-400">No expense data yet. Start tracking expenses!</p>
            </div>
          )}
        </Card>

        {/* Property Revenue */}
        <Card className="p-6 lg:col-span-2 bg-white shadow-xl border-0 hover:shadow-2xl transition-all">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Building2 className="h-5 w-5 text-purple-600" />
              </div>
              Top Properties by Revenue
            </h3>
            <span className="text-xs bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium">
              All Time
            </span>
          </div>
          {propertyRevenueData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={propertyRevenueData}>
                <defs>
                  <linearGradient id="colorBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.4}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                <Tooltip 
                  formatter={(value) => `₹${value.toLocaleString()}`}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                />
                <Bar dataKey="revenue" fill="url(#colorBar)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
              <p className="text-gray-400">No property revenue data yet. Start collecting rent!</p>
            </div>
          )}
        </Card>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-lg hover:shadow-xl transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 font-semibold uppercase tracking-wide">Total Collection</p>
              <p className="text-3xl font-bold text-green-900 mt-2">
                ₹{financial.total_collected.toLocaleString()}
              </p>
              <div className="flex items-center gap-1 mt-2">
                <TrendingUp className="h-4 w-4 text-green-600" />
                <span className="text-xs text-green-600 font-medium">All time earnings</span>
              </div>
            </div>
            <div className="bg-green-500 p-4 rounded-2xl shadow-lg">
              <TrendingUp className="h-8 w-8 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-red-50 to-pink-50 border-red-200 shadow-lg hover:shadow-xl transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700 font-semibold uppercase tracking-wide">Total Expenses</p>
              <p className="text-3xl font-bold text-red-900 mt-2">
                ₹{financial.total_expenses.toLocaleString()}
              </p>
              <div className="flex items-center gap-1 mt-2">
                <TrendingDown className="h-4 w-4 text-red-600" />
                <span className="text-xs text-red-600 font-medium">All time spending</span>
              </div>
            </div>
            <div className="bg-red-500 p-4 rounded-2xl shadow-lg">
              <TrendingDown className="h-8 w-8 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 shadow-lg hover:shadow-xl transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700 font-semibold uppercase tracking-wide">Net Profit</p>
              <p className="text-3xl font-bold text-blue-900 mt-2">
                ₹{financial.net_income.toLocaleString()}
              </p>
              <div className="flex items-center gap-1 mt-2">
                <IndianRupee className="h-4 w-4 text-blue-600" />
                <span className="text-xs text-blue-600 font-medium">Revenue - Expenses</span>
              </div>
            </div>
            <div className="bg-blue-500 p-4 rounded-2xl shadow-lg">
              <IndianRupee className="h-8 w-8 text-white" />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
